import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { money, todayET } from "@/lib/format";
import { Field } from "@/components/Field";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const METHODS = ["ACH", "Wire", "Check", "Cash", "Zelle", "Card", "Other"];

export const PaymentDialog = ({ open, onOpenChange, loanId, payment, summary, onSaved }) => {
  const editing = !!payment;
  const [f, setF] = useState({ date: todayET(), amount: "", method: "ACH", note: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setF(payment
      ? { date: payment.date, amount: (payment.amount_cents / 100).toFixed(2), method: payment.method, note: payment.note || "" }
      : { date: todayET(), amount: "", method: "ACH", note: "" });
  }, [open, payment]);

  const preview = useMemo(() => {
    const c = Math.round(+f.amount * 100);
    if (editing || !(c > 0) || f.date !== todayET() || !summary) return null;
    const fees = Math.min(c, summary.fees_outstanding_cents);
    const interest = Math.min(c - fees, summary.accrued_interest_cents);
    const principal = Math.min(c - fees - interest, summary.principal_balance_cents);
    return { fees, interest, principal, excess: c - fees - interest - principal };
  }, [f.amount, f.date, summary, editing]);

  const submit = async (e) => {
    e.preventDefault();
    if (f.amount === "" || isNaN(+f.amount) || Math.round(+f.amount * 100) <= 0) {
      setErrors({ amount: "Amount must be greater than 0" });
      return;
    }
    setBusy(true);
    try {
      const { data } = editing
        ? await api.put(`/payments/${payment.id}`, f)
        : await api.post(`/loans/${loanId}/payments`, f);
      const a = data.payment?.allocation;
      toast.success(editing ? "Payment updated — balances recalculated" : "Payment recorded", {
        description: a ? `Fees ${money(a.fees_cents)} · Interest ${money(a.interest_cents)} · Principal ${money(a.principal_cents)}${a.excess_cents ? ` · Overpayment ${money(a.excess_cents)}` : ""}` : undefined,
      });
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      const { message, fields } = apiError(err);
      setErrors(fields);
      if (!Object.keys(fields).length) toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{editing ? "Edit payment" : "Record payment"}</DialogTitle>
          <DialogDescription>Applied to outstanding fees, then accrued interest, then principal.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} noValidate className="space-y-5" data-testid="payment-form">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Payment date" name="date" error={errors.date}>
              <Input id="date" type="date" max={todayET()} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="h-10" data-testid="payment-date-input" />
            </Field>
            <Field label="Amount (USD)" name="amount" error={errors.amount}>
              <Input id="amount" inputMode="decimal" placeholder="0.00" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })}
                className={`num h-10 ${errors.amount ? "border-destructive" : ""}`} data-testid="payment-amount-input" autoFocus />
            </Field>
          </div>
          <Field label="Method" name="method" error={errors.method}>
            <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}>
              <SelectTrigger className="h-10" data-testid="payment-method-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {METHODS.map((m) => <SelectItem key={m} value={m} data-testid={`payment-method-${m}`}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Note" name="note">
            <Textarea id="note" rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Reference, check #, memo…" data-testid="payment-note-input" />
          </Field>
          {summary && !editing && (
            <div className="rounded-md border bg-muted/40 p-4 text-sm" data-testid="payment-allocation-preview">
              <div className="flex justify-between"><span className="text-muted-foreground">Current payoff</span><button type="button" title="Use payoff amount" onClick={() => setF((s) => ({ ...s, date: todayET(), amount: (summary.payoff_cents / 100).toFixed(2) }))}
                className="num font-semibold underline decoration-dotted underline-offset-4 hover:text-primary" data-testid="payment-dialog-payoff">{money(summary.payoff_cents)}</button></div>
              {preview && (
                <div className="mt-3 space-y-1.5 border-t pt-3">
                  <div className="flex justify-between"><span>→ Fees</span><span className="num">{money(preview.fees)}</span></div>
                  <div className="flex justify-between"><span>→ Interest</span><span className="num">{money(preview.interest)}</span></div>
                  <div className="flex justify-between"><span>→ Principal</span><span className="num">{money(preview.principal)}</span></div>
                  {preview.excess > 0 && <div className="flex justify-between font-semibold"><span>Overpayment</span><span className="num">{money(preview.excess)}</span></div>}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="payment-cancel-button">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="payment-submit-button">{busy && <Loader2 className="animate-spin" />}{editing ? "Save changes" : "Record payment"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
